import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { TrendingUp, BarChart3, Crown } from "lucide-react";
import { useTranslation } from 'react-i18next';

// Get backend URL
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  if (hostname === 'prospermind.com') {
    return `https://prospermind.com`;
  }
  if (hostname.includes('emergent.host')) {
    return `https://${hostname}`;
  }
  return process.env.REACT_APP_BACKEND_URL || `https://${hostname}`;
};

const API = `${getBackendUrl()}/api`;

/**
 * DiagnosisFlow Component
 * Financial diagnosis questionnaire with progress tracking
 * Extracted from App.js for better maintainability
 */
const DiagnosisFlow = ({ onComplete }) => {
  const { t, i18n } = useTranslation();
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [exactAmounts, setExactAmounts] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [progressRestored, setProgressRestored] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Load saved progress on component mount
  useEffect(() => {
    const loadSavedProgress = () => {
      try {
        const savedProgress = sessionStorage.getItem('diagnosisProgress');
        if (savedProgress) {
          const progressData = JSON.parse(savedProgress);
          setCurrentSection(progressData.currentSection || 0);
          setCurrentQuestion(progressData.currentQuestion || 0);
          setResponses(progressData.responses || {});
          setExactAmounts(progressData.exactAmounts || {});
          sessionStorage.removeItem('diagnosisProgress');
          toast.success(`Progress restored! Continuing from where you left off.`);
          setProgressRestored(true);
        }
      } catch (error) {
        console.error("Error loading saved progress:", error);
      }
    };
    loadSavedProgress();
  }, []);

  // Listen for language changes and force re-render
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // New behavioral questionnaire structure using translation keys
  const questionnaire = useMemo(() => [
    {
      section: t('questionnaire.sections.mindsetHabits.title'),
      description: t('questionnaire.sections.mindsetHabits.description'),
      questions: [
        {
          id: 1,
          type: "scale",
          question: t('questionnaire.sections.mindsetHabits.questions.control.question'),
          options: [
            { value: 5, label: t('questionnaire.sections.mindsetHabits.questions.control.options.always') },
            { value: 4, label: t('questionnaire.sections.mindsetHabits.questions.control.options.usually') },
            { value: 3, label: t('questionnaire.sections.mindsetHabits.questions.control.options.sometimes') },
            { value: 2, label: t('questionnaire.sections.mindsetHabits.questions.control.options.rarely') },
            { value: 1, label: t('questionnaire.sections.mindsetHabits.questions.control.options.never') }
          ]
        },
        {
          id: 2,
          type: "scale",
          question: t('questionnaire.sections.mindsetHabits.questions.tracking.question'),
          options: [
            { value: 5, label: t('questionnaire.sections.mindsetHabits.questions.tracking.options.daily') },
            { value: 4, label: t('questionnaire.sections.mindsetHabits.questions.tracking.options.weekly') },
            { value: 3, label: t('questionnaire.sections.mindsetHabits.questions.tracking.options.monthly') },
            { value: 2, label: t('questionnaire.sections.mindsetHabits.questions.tracking.options.occasionally') },
            { value: 1, label: t('questionnaire.sections.mindsetHabits.questions.tracking.options.never') }
          ]
        },
        {
          id: 3,
          type: "choice",
          question: t('questionnaire.sections.mindsetHabits.questions.extraIncome.question'),
          options: [
            { value: 5, label: t('questionnaire.sections.mindsetHabits.questions.extraIncome.options.save') },
            { value: 3, label: t('questionnaire.sections.mindsetHabits.questions.extraIncome.options.split') },
            { value: 1, label: t('questionnaire.sections.mindsetHabits.questions.extraIncome.options.spend') }
          ]
        },
        {
          id: 4,
          type: "choice",
          question: t('questionnaire.sections.mindsetHabits.questions.attitude.question'),
          options: [
            { value: 1, label: t('questionnaire.sections.mindsetHabits.questions.attitude.options.anxiety') },
            { value: 3, label: t('questionnaire.sections.mindsetHabits.questions.attitude.options.neutral') },
            { value: 5, label: t('questionnaire.sections.mindsetHabits.questions.attitude.options.freedom') }
          ]
        }
      ]
    },
    {
      section: t('questionnaire.sections.financialBehaviors.title'),
      description: t('questionnaire.sections.financialBehaviors.description'),
      questions: [
        {
          id: 5,
          type: "choice",
          question: t('questionnaire.sections.financialBehaviors.questions.monthlyOutcome.question'),
          options: [
            { value: 5, label: t('questionnaire.sections.financialBehaviors.questions.monthlyOutcome.options.surplus') },
            { value: 3, label: t('questionnaire.sections.financialBehaviors.questions.monthlyOutcome.options.breakeven') },
            { value: 1, label: t('questionnaire.sections.financialBehaviors.questions.monthlyOutcome.options.deficit') }
          ]
        },
        {
          id: 6,
          type: "range",
          question: t('questionnaire.sections.financialBehaviors.questions.savingRate.question'),
          options: [
            { value: 1, label: t('questionnaire.sections.financialBehaviors.questions.savingRate.options.none') },
            { value: 2, label: t('questionnaire.sections.financialBehaviors.questions.savingRate.options.little') },
            { value: 3, label: t('questionnaire.sections.financialBehaviors.questions.savingRate.options.some') },
            { value: 4, label: t('questionnaire.sections.financialBehaviors.questions.savingRate.options.good') },
            { value: 5, label: t('questionnaire.sections.financialBehaviors.questions.savingRate.options.significant') }
          ]
        },
        {
          id: 7,
          type: "scale",
          question: t('questionnaire.sections.financialBehaviors.questions.creditReliance.question'),
          options: [
            { value: 5, label: t('questionnaire.sections.financialBehaviors.questions.creditReliance.options.never') },
            { value: 4, label: t('questionnaire.sections.financialBehaviors.questions.creditReliance.options.rarely') },
            { value: 3, label: t('questionnaire.sections.financialBehaviors.questions.creditReliance.options.sometimes') },
            { value: 2, label: t('questionnaire.sections.financialBehaviors.questions.creditReliance.options.often') },
            { value: 1, label: t('questionnaire.sections.financialBehaviors.questions.creditReliance.options.almost') }
          ]
        },
        {
          id: 8,
          type: "choice",
          question: t('questionnaire.sections.financialBehaviors.questions.emergencyReady.question'),
          options: [
            { value: 5, label: t('questionnaire.sections.financialBehaviors.questions.emergencyReady.options.easy') },
            { value: 4, label: t('questionnaire.sections.financialBehaviors.questions.emergencyReady.options.tight') },
            { value: 3, label: t('questionnaire.sections.financialBehaviors.questions.emergencyReady.options.manage') },
            { value: 2, label: t('questionnaire.sections.financialBehaviors.questions.emergencyReady.options.credit') },
            { value: 1, label: t('questionnaire.sections.financialBehaviors.questions.emergencyReady.options.borrow') }
          ]
        }
      ]
    },
    {
      section: t('questionnaire.sections.cashFlow.title'),
      description: t('questionnaire.sections.cashFlow.description'),
      questions: [
        {
          id: 9,
          type: "range_with_exact",
          question: t('questionnaire.sections.cashFlow.questions.monthlyIncome.question'),
          options: [
            { value: "exact", label: t('questionnaire.sections.cashFlow.questions.monthlyIncome.options.exact') },
            { value: 1, label: t('questionnaire.sections.cashFlow.questions.monthlyIncome.options.under2k') },
            { value: 2, label: t('questionnaire.sections.cashFlow.questions.monthlyIncome.options.2k-4k') },
            { value: 3, label: t('questionnaire.sections.cashFlow.questions.monthlyIncome.options.4k-7k') },
            { value: 4, label: t('questionnaire.sections.cashFlow.questions.monthlyIncome.options.7k-12k') },
            { value: 5, label: t('questionnaire.sections.cashFlow.questions.monthlyIncome.options.over12k') }
          ]
        },
        {
          id: 10,
          type: "range_with_exact",
          question: t('questionnaire.sections.cashFlow.questions.monthlyExpenses.question'),
          options: [
            { value: "exact", label: t('questionnaire.sections.cashFlow.questions.monthlyExpenses.options.exact') },
            { value: 1, label: t('questionnaire.sections.cashFlow.questions.monthlyExpenses.options.under1k') },
            { value: 2, label: t('questionnaire.sections.cashFlow.questions.monthlyExpenses.options.1k-2.5k') },
            { value: 3, label: t('questionnaire.sections.cashFlow.questions.monthlyExpenses.options.2.5k-4k') },
            { value: 4, label: t('questionnaire.sections.cashFlow.questions.monthlyExpenses.options.4k-6k') },
            { value: 5, label: t('questionnaire.sections.cashFlow.questions.monthlyExpenses.options.over6k') }
          ]
        },
        {
          id: 11,
          type: "range_with_exact",
          question: t('questionnaire.sections.cashFlow.questions.savings.question'),
          options: [
            { value: "exact", label: t('questionnaire.sections.cashFlow.questions.savings.options.exact') },
            { value: 1, label: t('questionnaire.sections.cashFlow.questions.savings.options.under500') },
            { value: 2, label: t('questionnaire.sections.cashFlow.questions.savings.options.500-2k') },
            { value: 3, label: t('questionnaire.sections.cashFlow.questions.savings.options.2k-10k') },
            { value: 4, label: t('questionnaire.sections.cashFlow.questions.savings.options.10k-25k') },
            { value: 5, label: t('questionnaire.sections.cashFlow.questions.savings.options.over25k') }
          ]
        },
        {
          id: 12,
          type: "range_with_exact",
          question: t('questionnaire.sections.cashFlow.questions.debt.question'),
          options: [
            { value: "exact", label: t('questionnaire.sections.cashFlow.questions.debt.options.exact') },
            { value: 5, label: t('questionnaire.sections.cashFlow.questions.debt.options.none') },
            { value: 4, label: t('questionnaire.sections.cashFlow.questions.debt.options.under5k') },
            { value: 3, label: t('questionnaire.sections.cashFlow.questions.debt.options.5k-15k') },
            { value: 2, label: t('questionnaire.sections.cashFlow.questions.debt.options.15k-30k') },
            { value: 1, label: t('questionnaire.sections.cashFlow.questions.debt.options.over30k') }
          ]
        }
      ]
    },
    {
      section: t('questionnaire.sections.lifestyle.title'),
      description: t('questionnaire.sections.lifestyle.description'),
      questions: [
        {
          id: 13,
          type: "range",
          question: t('questionnaire.sections.lifestyle.questions.luxurySpending.question'),
          options: [
            { value: 5, label: t('questionnaire.sections.lifestyle.questions.luxurySpending.options.minimal') },
            { value: 4, label: t('questionnaire.sections.lifestyle.questions.luxurySpending.options.low') },
            { value: 3, label: t('questionnaire.sections.lifestyle.questions.luxurySpending.options.moderate') },
            { value: 2, label: t('questionnaire.sections.lifestyle.questions.luxurySpending.options.significant') },
            { value: 1, label: t('questionnaire.sections.lifestyle.questions.luxurySpending.options.high') }
          ]
        },
        {
          id: 14,
          type: "choice",
          question: t('questionnaire.sections.lifestyle.questions.lifestyleChoice.question'),
          options: [
            { value: 5, label: t('questionnaire.sections.lifestyle.questions.lifestyleChoice.options.basics') },
            { value: 3, label: t('questionnaire.sections.lifestyle.questions.lifestyleChoice.options.normal') },
            { value: 1, label: t('questionnaire.sections.lifestyle.questions.lifestyleChoice.options.luxury') }
          ]
        }
      ]
    },
    {
      section: t('questionnaire.sections.motivation.title'),
      description: t('questionnaire.sections.motivation.description'),
      questions: [
        {
          id: 15,
          type: "choice",
          question: t('questionnaire.sections.motivation.questions.currentState.question'),
          options: [
            { value: 1, label: t('questionnaire.sections.motivation.questions.currentState.options.survival') },
            { value: 2, label: t('questionnaire.sections.motivation.questions.currentState.options.paycheck') },
            { value: 3, label: t('questionnaire.sections.motivation.questions.currentState.options.stable') },
            { value: 4, label: t('questionnaire.sections.motivation.questions.currentState.options.growing') },
            { value: 5, label: t('questionnaire.sections.motivation.questions.currentState.options.freedom') }
          ]
        },
        {
          id: 16,
          type: "choice",
          question: t('questionnaire.sections.motivation.questions.topGoal.question'),
          options: [
            { value: 1, label: t('questionnaire.sections.motivation.questions.topGoal.options.payoff') },
            { value: 2, label: t('questionnaire.sections.motivation.questions.topGoal.options.emergency') },
            { value: 3, label: t('questionnaire.sections.motivation.questions.topGoal.options.invest') },
            { value: 4, label: t('questionnaire.sections.motivation.questions.topGoal.options.retire') }
          ]
        }
      ]
    }
  ], [t, i18n.language]);

  const totalQuestions = questionnaire.reduce((sum, section) => sum + section.questions.length, 0);
  const currentQuestionNumber = questionnaire.slice(0, currentSection).reduce((sum, section) => sum + section.questions.length, 0) + currentQuestion + 1;
  const progress = (currentQuestionNumber / totalQuestions) * 100;

  const currentSectionData = questionnaire[currentSection];
  const currentQuestionData = currentSectionData.questions[currentQuestion];

  const handleAnswer = (value) => {
    if (value === "exact") {
      setResponses(prev => ({
        ...prev,
        [currentQuestionData.id]: 999
      }));
    } else {
      setResponses(prev => ({
        ...prev,
        [currentQuestionData.id]: parseInt(value)
      }));
      setExactAmounts(prev => {
        const newExact = { ...prev };
        delete newExact[currentQuestionData.id];
        return newExact;
      });
    }
  };

  const handleExactAmount = (questionId, amount) => {
    const numericAmount = parseFloat(amount) || 0;
    setExactAmounts(prev => ({
      ...prev,
      [questionId]: numericAmount
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion < currentSectionData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentSection < questionnaire.length - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentQuestion(0);
    } else {
      submitDiagnosis();
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      setCurrentQuestion(questionnaire[currentSection - 1].questions.length - 1);
    }
  };

  const calculateResults = (responses, exactAmounts) => {
    const mindsetScore = (responses[1] + responses[2] + responses[3] + responses[4]) / 4;
    const behaviorScore = (responses[5] + responses[6] + responses[7] + responses[8]) / 4;
    
    const getFinancialValue = (questionId, defaultValue) => {
      if (responses[questionId] === 999 && exactAmounts[questionId]) {
        const exactAmount = exactAmounts[questionId];
        if (questionId === 9) {
          if (exactAmount < 2000) return 1;
          if (exactAmount <= 4000) return 2;
          if (exactAmount <= 7000) return 3;
          if (exactAmount <= 12000) return 4;
          return 5;
        } else if (questionId === 10) {
          if (exactAmount < 1000) return 1;
          if (exactAmount <= 2500) return 2;
          if (exactAmount <= 4000) return 3;
          if (exactAmount <= 6000) return 4;
          return 5;
        } else if (questionId === 11) {
          if (exactAmount < 500) return 1;
          if (exactAmount <= 2000) return 2;
          if (exactAmount <= 10000) return 3;
          if (exactAmount <= 25000) return 4;
          return 5;
        } else if (questionId === 12) {
          if (exactAmount === 0) return 5;
          if (exactAmount < 5000) return 4;
          if (exactAmount <= 15000) return 3;
          if (exactAmount <= 30000) return 2;
          return 1;
        }
      }
      return responses[questionId] || defaultValue;
    };

    const incomeLevel = getFinancialValue(9, 3);
    const expenseLevel = getFinancialValue(10, 3);
    const savingsLevel = getFinancialValue(11, 2);
    const debtLevel = getFinancialValue(12, 3);
    
    const lifestyleScore = (responses[13] + responses[14]) / 2;
    const motivationScore = (responses[15] + responses[16]) / 2;

    const surplusDeficit = incomeLevel - expenseLevel;
    
    let ladderStage = "independence";
    if (surplusDeficit <= -2 && debtLevel <= 2) ladderStage = "dependence";
    else if (surplusDeficit <= -1) ladderStage = "productivity";
    else if (surplusDeficit === 0 && savingsLevel <= 2) ladderStage = "independence";
    else if (surplusDeficit >= 1 && savingsLevel >= 3) ladderStage = "stability";
    else if (savingsLevel >= 4 && responses[6] >= 4) ladderStage = "security";
    else if (savingsLevel >= 5 && responses[6] >= 5) ladderStage = "freedom";
    else if (savingsLevel >= 5 && responses[6] >= 5 && responses[15] >= 5) ladderStage = "abundance";

    let cyclePosition = "zero";
    if (responses[5] === 1 || responses[7] <= 2) cyclePosition = "loss";
    else if (responses[5] === 5 && responses[6] >= 4) cyclePosition = "gains";
    
    const diagnosisProfile = {
      beliefs: mindsetScore >= 4 ? "strong" : mindsetScore >= 3 ? "developing" : "needsWork",
      habits: behaviorScore >= 4 ? "strong" : behaviorScore >= 3 ? "developing" : "needsWork",
      skills: motivationScore >= 4 ? "advanced" : motivationScore >= 3 ? "intermediate" : "beginner",
      tools: responses[2] >= 4 ? "good" : responses[2] >= 3 ? "basic" : "limited"
    };

    return {
      ladder_stage: ladderStage,
      cycle_position: cyclePosition,
      diagnosis_profile: diagnosisProfile,
      overall_score: (mindsetScore + behaviorScore + motivationScore) / 3,
      responses,
      exact_amounts: exactAmounts
    };
  };

  const handleSaveAndExit = async () => {
    try {
      const progressData = {
        currentSection,
        currentQuestion,
        responses,
        exactAmounts,
        timestamp: new Date().toISOString()
      };
      
      sessionStorage.setItem('diagnosisProgress', JSON.stringify(progressData));
      toast.success("Progress saved! You can continue your assessment later.");
      onComplete();
      
    } catch (error) {
      console.error("Error saving progress:", error);
      toast.error("Failed to save progress, but returning to dashboard");
      onComplete();
    }
  };

  const submitDiagnosis = async () => {
    try {
      console.log("Starting diagnosis submission...");
      const calculatedResults = calculateResults(responses, exactAmounts);
      console.log("Calculated results:", calculatedResults);
      
      const diagnosisData = {
        responses: Object.entries(responses).map(([questionId, answer]) => ({
          question_id: parseInt(questionId),
          answer: answer
        })),
        results: calculatedResults,
        exact_amounts: exactAmounts
      };
      
      console.log("Diagnosis data to submit:", diagnosisData);
      console.log("API endpoint:", `${API}/diagnosis/submit`);

      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      console.log("Auth token found:", token ? token.substring(0, 20) + "..." : "No token");

      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post(`${API}/diagnosis/submit`, diagnosisData, config);
      console.log("API response:", response.data);
      
      setResults(calculatedResults);
      setShowResults(true);
      toast.success("Diagnosis completed successfully!");
      
    } catch (error) {
      console.error("Detailed error submitting diagnosis:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Full error object:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      const errorMessage = error.response?.data?.detail 
        ? (Array.isArray(error.response.data.detail) 
           ? error.response.data.detail.map(err => err.msg || err).join(', ')
           : error.response.data.detail)
        : error.message;
      
      toast.error(`Failed to submit diagnosis: ${errorMessage}`);
    }
  };

  if (showResults && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="text-center mb-12">
            <h1 className="text-2xl font-bold text-black mb-4">{t('questionnaire.results.title')}</h1>
            <p className="text-lg text-slate-600">{t('questionnaire.results.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Ladder of Wealth Stage */}
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <Crown className="w-4 h-4 text-white" />
                  </div>
                  {t('questionnaire.results.wealthStage')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-700 capitalize mb-2">
                  {results.ladder_stage}
                </div>
                <p className="text-sm text-slate-600">
                  {t('questionnaire.results.ladderDescription')}
                </p>
              </CardContent>
            </Card>

            {/* Money Cycle Position */}
            <Card className="border-emerald-200 bg-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  {t('questionnaire.results.moneyCycle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-emerald-700 capitalize mb-2">
                  {results.cycle_position}
                </div>
                <p className="text-sm text-slate-600">
                  {t('questionnaire.results.cycleDescription')}
                </p>
              </CardContent>
            </Card>

            {/* Financial Diagnosis */}
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-white" />
                  </div>
                  {t('questionnaire.results.diagnosisScore')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-700 mb-2">
                  {Math.round(results.overall_score * 20)}/100
                </div>
                <p className="text-sm text-slate-600">
                  {t('questionnaire.results.maturityDescription')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Profile Breakdown */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{t('questionnaire.results.yourFinancialProfile')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-lg font-semibold text-slate-700">{t('questionnaire.results.beliefs')}</div>
                  <div className="text-sm text-slate-600">{t(`questionnaire.results.profile.${results.diagnosis_profile.beliefs}`)}</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-slate-700">{t('questionnaire.results.habits')}</div>
                  <div className="text-sm text-slate-600">{t(`questionnaire.results.profile.${results.diagnosis_profile.habits}`)}</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-slate-700">{t('questionnaire.results.skills')}</div>
                  <div className="text-sm text-slate-600">{t(`questionnaire.results.profile.${results.diagnosis_profile.skills}`)}</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-slate-700">{t('questionnaire.results.tools')}</div>
                  <div className="text-sm text-slate-600">{t(`questionnaire.results.profile.${results.diagnosis_profile.tools}`)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Next Steps CTA */}
          <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">{t('questionnaire.results.readyToGetAccurate')}</h3>
              <p className="text-lg mb-6">
                {t('questionnaire.results.startingPoint')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => {
                    onComplete();
                    toast.success(t('questionnaire.results.diagnosisCompleted'));
                  }}
                  className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold"
                >
                  {t('questionnaire.results.goToBudget')}
                </Button>
                <Button 
                  onClick={onComplete}
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-3 text-lg font-semibold"
                >
                  {t('questionnaire.results.backToDashboard')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-black mb-2">{t('questionnaire.title')}</h1>
              <p className="text-slate-600">
                {t('questionnaire.ui.step')} {currentSection + 1} {t('questionnaire.ui.of')} {questionnaire.length} • {t('questionnaire.ui.question')} {currentQuestionNumber} {t('questionnaire.ui.of')} {totalQuestions} • {Math.round(progress)}% {t('questionnaire.ui.complete')}
              </p>
            </div>
            
            {/* Save and Exit Button */}
            <Button
              variant="outline"
              onClick={handleSaveAndExit}
              className="border-slate-300 text-slate-600 hover:bg-slate-50 px-4 py-2 text-sm"
            >
              {t('questionnaire.ui.saveAndExit')}
            </Button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-6">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className="bg-gradient-to-r from-emerald-500 to-teal-600 h-4 rounded-full transition-all duration-500 ease-in-out flex items-center justify-end pr-2"
                style={{ width: `${progress}%` }}
              >
                <span className="text-white text-xs font-medium">{Math.round(progress)}%</span>
              </div>
            </div>
          </div>
        </div>

        <Card className="shadow-lg border-0 backdrop-blur-sm bg-white/90">
          <CardHeader>
            <CardTitle className="text-xl text-slate-800">
              <Badge variant="outline" className="mb-4">{currentSectionData.section}</Badge>
            </CardTitle>
            {currentQuestion === 0 && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 italic">
                  {currentSectionData.description}
                </p>
              </div>
            )}
            <CardDescription className="text-lg text-slate-700 leading-relaxed">
              {currentQuestionData.question}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentQuestionData.options.map((option) => (
                <div key={option.value}>
                  <label
                    className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all hover:bg-emerald-50 ${
                      responses[currentQuestionData.id] === option.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestionData.id}`}
                      value={option.value}
                      checked={responses[currentQuestionData.id] === option.value}
                      onChange={(e) => handleAnswer(e.target.value)}
                      className="w-4 h-4 text-emerald-600 mr-3"
                    />
                    <span className="text-slate-700">{option.label}</span>
                  </label>
                  {/* Show exact amount input field when "exact" option is selected */}
                  {option.value === "exact" && responses[currentQuestionData.id] === 999 && (
                    <div className="mt-3 ml-7 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg">
                      <label className="block text-sm font-medium text-emerald-800 mb-2">
                        {t('questionnaire.ui.exactAmount.label')}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-emerald-700 font-semibold">$</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          placeholder={
                            currentQuestionData.id === 9 ? t('questionnaire.ui.exactAmount.placeholder.income') :
                            currentQuestionData.id === 10 ? t('questionnaire.ui.exactAmount.placeholder.expenses') :
                            currentQuestionData.id === 11 ? t('questionnaire.ui.exactAmount.placeholder.savings') :
                            currentQuestionData.id === 12 ? t('questionnaire.ui.exactAmount.placeholder.debt') : "0"
                          }
                          value={exactAmounts[currentQuestionData.id] || ''}
                          onChange={(e) => handleExactAmount(currentQuestionData.id, e.target.value)}
                          className="flex-1 px-4 py-2 border border-emerald-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-emerald-900 placeholder-emerald-400"
                          autoFocus
                        />
                      </div>
                      <p className="text-xs text-emerald-600 mt-2">
                        {t('questionnaire.ui.exactAmount.tooltip')}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                onClick={previousQuestion}
                disabled={currentSection === 0 && currentQuestion === 0}
              >
                {t('questionnaire.ui.previous')}
              </Button>
              <Button
                onClick={nextQuestion}
                disabled={!responses[currentQuestionData.id] || 
                         (responses[currentQuestionData.id] === 999 && !exactAmounts[currentQuestionData.id])}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {currentSection === questionnaire.length - 1 && currentQuestion === currentSectionData.questions.length - 1 
                  ? t('questionnaire.ui.completeAssessment')
                  : t('questionnaire.ui.nextQuestion')
                }
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DiagnosisFlow;
